'use client';

import { useCallback, useRef, useState, useEffect } from 'react';
import { eventsFormData, formStatusData, SelectOptions } from '@lib/types';
import TextInput from '@components/Forms/FormComponents/TextInput/textInput';
import TextAreaInput from '@components/Forms/FormComponents/TextAreaInput/textAreaInput';
import NumInput from '@components/Forms/FormComponents/NumInput/numInput';
import SelectInput from '@components/Forms/FormComponents/SelectInput/selectInput';
import LoadingSpinner from '@components/Forms/FormComponents/LoadingSpinner/loadingSpinner';
import buttonStyles from '@components/SectionComponents/Buttons/button.module.scss';
import FormStatus from '@components/Forms/FormComponents/FormStatus/formStatus';

import formStyles from '@components/Forms/FormComponents/formStyles.module.scss';
import { executeRecaptcha } from '@components/Forms/FormComponents/RecaptchaFormComponent/RecaptchaClientFunctions';
import GTMEvent from '@lib/gtm_events/GTMEvent';

const FORM_CONFIG = {
    instructionsId: 'form-instructions',
    initialState: {
        name: '',
        email: '',
        phone: '',
        numGuests: '',
        message: '',
        title: '',
    },
    btnDisable: false,
    selectedEvent: '',
};

const EventsForm = ({
    eventOptionsFormPage,
}: {
    eventOptionsFormPage: SelectOptions;
}) => {
    const [formObject, setFormObject] = useState(FORM_CONFIG.initialState);
    const [btnDisable, setBtnDisable] = useState(FORM_CONFIG.btnDisable);
    const [selectedEvent, setSelectedEvent] = useState(
        eventOptionsFormPage[0]?.value || FORM_CONFIG.selectedEvent,
    );

    const topOfFormRef = useRef<HTMLFormElement>(null);
    const [formStatusData, setFormStatusData] = useState<formStatusData>({
        message: '',
        show: false,
        colour: 'red',
    });

    const scrollToTopOfFormRef = useCallback(() => {
    const target = document.getElementById('events-form');
    target?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
    });
}, []);

    useEffect(() => {
        const onEventSelect = (e: Event) => {
            const title = (e as CustomEvent<string>).detail;
            setSelectedEvent(title);
            scrollToTopOfFormRef();
        };

        window.addEventListener('event-select', onEventSelect);
        return () => window.removeEventListener('event-select', onEventSelect);
    }, []);

    useEffect(() => {
        window.addEventListener('event-form-scroll', scrollToTopOfFormRef);
        return () =>
            window.removeEventListener(
                'event-form-scroll',
                scrollToTopOfFormRef,
            );
    }, [scrollToTopOfFormRef]);

    const handleReset = useCallback(() => {
        setFormObject(FORM_CONFIG.initialState);
        setBtnDisable(FORM_CONFIG.btnDisable);
        setSelectedEvent(
            eventOptionsFormPage[0]?.value || FORM_CONFIG.selectedEvent,
        );
        scrollToTopOfFormRef();
    }, []);

    const apiHandler = async () => {
        const token = await executeRecaptcha();

        const eventsFormData: eventsFormData = {
            name: formObject.name,
            email: formObject.email,
            phone: formObject.phone,
            selectedEvent: selectedEvent,
            numGuests: formObject.numGuests,
            message: formObject.message,
            recaptchaToken: token,
            title: formObject.title,
        };

        // https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch
        const response = await fetch('/api/eventsForm', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(eventsFormData),
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'An unknown error occurred');
        }
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        setBtnDisable(true);

        if (
            !formObject.name ||
            !formObject.email ||
            !formObject.phone ||
            !selectedEvent ||
            !formObject.numGuests ||
            !formObject.message
        ) {
            setFormStatusData({
                message:
                    'Please complete all the required fields before submitting',
                show: true,
                colour: 'orange',
            });
            scrollToTopOfFormRef();
            setBtnDisable(false);
            return;
        }

        try {
            await apiHandler();

            GTMEvent('form_submit', { form_name: 'Events' });

            setFormStatusData({
                message:
                    'Thank you for contacting us, we will be in touch shortly',
                show: true,
                colour: 'green',
            });
            handleReset();
            setBtnDisable(false);
        } catch (e) {
            const err = e as Error;
            setFormStatusData({
                message: `Crikey! Something went wrong: ${err.message}`,
                show: true,
                colour: 'red',
            });
            setBtnDisable(false);
        }
    };

    const handleInvalid = () => {
        if (!topOfFormRef.current) return;

        setTimeout(() => {
            scrollToTopOfFormRef();
        });

        setFormStatusData({
            message:
                'Please complete all the required fields before submitting',
            show: true,
            colour: 'orange',
        });
    };

    return (
        <>
            <form
                id="event-form"
                ref={topOfFormRef}
                onSubmit={handleSubmit}
                onInvalid={handleInvalid}
                style={{ width: '100%', maxWidth: '1170px' }}
                className={formStyles.scroll}
            >
                {btnDisable && (
                    <div role="status" aria-live="polite">
                        <LoadingSpinner active={btnDisable} />
                    </div>
                )}

                {formStatusData.show && (
                    <div aria-live="polite">
                        {/* Show any error or success messages */}
                        <FormStatus
                            message={formStatusData.message}
                            colour={formStatusData.colour}
                        />
                    </div>
                )}

                <p
                    id={FORM_CONFIG.instructionsId}
                    className={formStyles.formInstructions}
                >
                    <i>All fields are required.&nbsp;</i>
                    <span className={formStyles.asterisk}>*</span>
                </p>

                {/* Personal Details */}

                <TextInput
                    value={formObject.name}
                    onChange={(value) =>
                        setFormObject((prev) => ({ ...prev, name: value }))
                    }
                    id="fname"
                    name="fname"
                    title="Full Name"
                    placeholder="Enter your full name"
                    autoComplete="name"
                    required={true}
                    showRequired={false}
                    formInstructionsId={FORM_CONFIG.instructionsId}
                />
                {/** Honeypot field to try catch spam bots */}
                <TextInput
                    value={formObject.title}
                    onChange={(value) =>
                        setFormObject((prev) => ({ ...prev, title: value }))
                    }
                    id="title"
                    name="title"
                    title="Title"
                    placeholder="Enter your title"
                    autoComplete="off"
                    hideOffScreen={true}
                    tabIndex={-1}
                />
                <TextInput
                    value={formObject.email}
                    onChange={(value) =>
                        setFormObject((prev) => ({ ...prev, email: value }))
                    }
                    id="email"
                    name="email"
                    title="Email"
                    type="email"
                    placeholder="Enter your email address"
                    autoComplete="email"
                    required={true}
                    showRequired={false}
                    formInstructionsId={FORM_CONFIG.instructionsId}
                />
                <TextInput
                    value={formObject.phone}
                    onChange={(value) => {
                        const filtered = value.replace(/[^0-9()+\s]/g, '');
                        setFormObject((prev) => ({ ...prev, phone: filtered }));
                    }}
                    id="phone"
                    name="phone"
                    title="Phone Number"
                    type="tel"
                    placeholder="(+61) 432 123 321"
                    autoComplete="tel"
                    required={true}
                    showRequired={false}
                    formInstructionsId={FORM_CONFIG.instructionsId}
                />

                {/* Event Details */}

                <SelectInput
                    value={selectedEvent}
                    onChange={(value) => setSelectedEvent(value)}
                    id="selectEventName"
                    name="selectEventName"
                    title="Event Name"
                    options={eventOptionsFormPage}
                    required={true}
                    showRequired={false}
                    formInstructionsId={FORM_CONFIG.instructionsId}
                />
                <NumInput
                    value={formObject.numGuests}
                    onChange={(value) =>
                        setFormObject((prev) => ({ ...prev, numGuests: value }))
                    }
                    id="numguests"
                    name="numguests"
                    title="Number of Guests"
                    max={1000}
                    required={true}
                    showRequired={false}
                    formInstructionsId={FORM_CONFIG.instructionsId}
                />
                <TextAreaInput
                    value={formObject.message}
                    onChange={(value) =>
                        setFormObject((prev) => ({ ...prev, message: value }))
                    }
                    id="message"
                    name="message"
                    title="Your Message"
                    placeholder="Your Message"
                    required={true}
                    showRequired={false}
                    formInstructionsId={FORM_CONFIG.instructionsId}
                    maxLength={5000}
                />

                <br />

                <button
                    id="submit"
                    type="submit"
                    className={`${buttonStyles.btn}`}
                    disabled={btnDisable}
                >
                    <span id="button-text">SUBMIT</span>
                </button>
            </form>
        </>
    );
};

export default EventsForm;
